import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import './RoomManagement.css';

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://intranet.nesimltd.co.ke' : 'http://localhost:5000');

const RoomManagement = ({ room, onRoomUpdate, onClose, onCreateRoom, onRoomDelete, isCreating: propIsCreating = false }) => {
  const { user, token } = useContext(AuthContext);
  const [liveRoom, setLiveRoom] = useState(room);
  const [showAddMembersPanel, setShowAddMembersPanel] = useState(false);
  const [openMemberMenu, setOpenMemberMenu] = useState(null);
  const [isFavourite, setIsFavourite] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  useEffect(() => {
    setLiveRoom(room);
  }, [room]);

  // Sync internal isCreating state with prop
  useEffect(() => {
    setIsCreating(propIsCreating);
  }, [propIsCreating]);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(propIsCreating);
  const [editForm, setEditForm] = useState({
    name: room?.name || '',
    description: room?.description || '',
    isPrivate: room?.isPrivate || false
  });
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    isPrivate: false,
    type: 'public'
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [forceRender, setForceRender] = useState(0);

  // Listen for room updates from parent component - no separate socket needed
  useEffect(() => {
    console.log('🏠 RoomManagement - Room prop updated:', {
      roomId: room?._id,
      roomName: room?.name,
      memberCount: room?.members?.length,
      members: room?.members?.map(m => m.name || m.email)
    });
    setLiveRoom(room);
  }, [room]);

  // Force re-render when room members change
  useEffect(() => {
    console.log('🏠 RoomManagement - Forcing update due to member change:', {
      memberCount: room?.members?.length,
      members: room?.members?.map(m => m.name || m.email),
      liveRoomMemberCount: liveRoom?.members?.length,
      liveRoomMembers: liveRoom?.members?.map(m => m.name || m.email)
    });
    if (room) {
      setLiveRoom({...room}); // Force new object reference
    }
  }, [room?.members?.length]);

  // Debug logging for room object
  useEffect(() => {
    console.log('🔍 RoomManagement - Room object debug:', {
      room: liveRoom,
      roomId: liveRoom?._id,
      roomName: liveRoom?.name,
      roomType: liveRoom?.type,
      roomAdmins: liveRoom?.admins,
      roomCreator: liveRoom?.creator,
      roomMembers: liveRoom?.members,
      fullRoomObject: JSON.stringify(liveRoom, null, 2)
    });
  }, [liveRoom]);
  
  // Monitor liveRoom changes specifically
  useEffect(() => {
    console.log('🔍 RoomManagement - liveRoom changed:', {
      memberCount: liveRoom?.members?.length,
      members: liveRoom?.members?.map(m => ({ id: m._id, name: m.name, email: m.email })),
      timestamp: new Date().toISOString()
    });
  }, [liveRoom?.members?.length]);

  // For DMs, both users should be considered admins for chat clearing purposes
  const isAdmin = liveRoom?.type === 'direct' 
    ? liveRoom?.members?.some(member => member._id?.toString() === (user?._id || user?.id)?.toString())
    : liveRoom?.admins?.some(admin => admin._id?.toString() === (user?._id || user?.id)?.toString());
  const isCreator = liveRoom?.creator?._id?.toString() === (user?._id || user?.id)?.toString();

  // Debug logging for admin check
  useEffect(() => {
    console.log('🔍 RoomManagement - Admin check debug:', {
      roomId: liveRoom?._id,
      roomType: liveRoom?.type,
      roomAdmins: liveRoom?.admins,
      roomCreator: liveRoom?.creator,
      roomMembers: liveRoom?.members,
      currentUser: user,
      currentUserId: user?._id || user?.id,
      isAdmin,
      isCreator,
      adminCheckLogic: liveRoom?.type === 'direct' ? 'DM: checking if user is member' : 'Regular room: checking if user is admin',
      // Additional debugging for admin operations
      userIsInAdmins: liveRoom?.admins?.some(admin => admin._id === (user?._id || user?.id)),
      userIsCreator: liveRoom?.creator?._id === (user?._id || user?.id),
      adminIds: liveRoom?.admins?.map(a => a._id),
      creatorId: liveRoom?.creator?._id
    });
  }, [liveRoom, user, isAdmin, isCreator]);

  useEffect(() => {
    // Fetch users when component mounts to populate the add member section
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/chat/users`, {
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const usersData = await response.json();
        setUsers(usersData);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/chat/rooms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...createForm,
          members: selectedUsers
        })
      });

      if (response.ok) {
        const result = await response.json();
        onCreateRoom(result.room);
        setIsCreating(false);
        setCreateForm({ name: '', description: '', isPrivate: false, type: 'public' });
        setSelectedUsers([]);
      } else {
        const error = await response.json();
        alert(error.message);
      }
    } catch (error) {
      console.error('Error creating room:', error);
      alert('Failed to create room');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/chat/rooms/${liveRoom._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        const result = await response.json();
        onRoomUpdate(result.room);
        setIsEditing(false);
      } else {
        const error = await response.json();
        alert(error.message);
      }
    } catch (error) {
      console.error('Error updating room:', error);
      alert('Failed to update room');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSelectedMembers = async () => {
    if (selectedUsers.length === 0) return;

    setLoading(true);
    try {
      // Add each selected user to the room
      const addPromises = selectedUsers.map(userId => 
        fetch(`${API_BASE}/api/chat/rooms/${room._id}/members`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ userId })
        })
      );

      const responses = await Promise.all(addPromises);
      const failedResponses = responses.filter(response => !response.ok);

      if (failedResponses.length === 0) {
        // All users added successfully, refresh room data
        const roomResponse = await fetch(`${API_BASE}/api/chat/rooms/${liveRoom._id}`, {
          headers: {
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`
          }
        });
        if (roomResponse.ok) {
          const roomData = await roomResponse.json();
          onRoomUpdate(roomData.room);
        }
        setSelectedUsers([]);
        alert(`${selectedUsers.length} member(s) added successfully.`);
      } else {
        // Some users failed to be added
        alert(`Failed to add ${failedResponses.length} member(s). Please try again.`);
      }
    } catch (error) {
      console.error('Error adding selected members:', error);
      alert('Failed to add selected members');
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteToAdmin = async (memberId) => {
    console.log('🚀 Attempting to promote member:', {
      memberId,
      roomId: room._id,
      currentUser: user,
      roomAdmins: room.admins,
      roomCreator: room.creator
    });
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/chat/rooms/${liveRoom._id}/admins`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId: memberId })
      });

      console.log('📡 Promote response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Promote successful:', result);
        onRoomUpdate(result.room);
        alert('Member promoted to admin');
      } else {
        const error = await response.json();
        console.error('Promote to admin error:', error);
        alert(`Failed to promote member: ${error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error promoting member:', error);
      alert(`Failed to promote member: ${error.message || 'Network error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoteAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to demote this admin to member?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/chat/rooms/${liveRoom._id}/admins/${adminId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        onRoomUpdate(result.room);
        alert('Admin demoted to member');
      } else {
        const error = await response.json();
        console.error('Demote admin error:', error);
        alert(`Failed to demote admin: ${error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error demoting admin:', error);
      alert(`Failed to demote admin: ${error.message || 'Network error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    console.log('🚨 REMOVE BUTTON CLICKED - memberId:', memberId);
    console.log('🚨 Current liveRoom:', liveRoom);
    console.log('🚨 User info:', user);
    console.log('🚨 Is admin check:', {
      isAdmin,
      userRole: user?.role,
      roomAdmins: liveRoom?.admins?.map(a => ({ id: a._id, name: a.name })),
      userId: user?._id || user?.id
    });
    
    if (!window.confirm('Are you sure you want to remove this member?')) {
      console.log('🚨 User cancelled removal');
      return;
    }

    console.log('🗑️ RoomManagement - Starting member removal:', {
      memberId,
      roomId: liveRoom._id,
      currentMemberCount: liveRoom.members?.length
    });

    setLoading(true);
    try {
      const url = `${API_BASE}/api/chat/rooms/${liveRoom._id}/members/${memberId}`;
      console.log('🌐 Making DELETE request to:', url);
      console.log('🔑 Token:', localStorage.getItem('token') ? 'Present' : 'Missing');
      console.log('🔍 Request details:', {
        roomId: liveRoom._id,
        memberId: memberId,
        url: url,
        method: 'DELETE'
      });
      
      console.log('🌐 Making API call with details:', {
        url,
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(token || localStorage.getItem('token'))?.substring(0, 20)}...`,
          'Content-Type': 'application/json'
        }
      });
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🗑️ RoomManagement - Remove member response:', response.status);
      console.log('🗑️ RoomManagement - Response headers:', Object.fromEntries(response.headers.entries()));

      if (response.ok) {
        const result = await response.json();
        console.log('🗑️ RoomManagement - Remove member result:', {
          newMemberCount: result.room?.members?.length,
          members: result.room?.members?.map(m => m.name || m.email),
          roomId: result.room?._id,
          fullRoom: result.room
        });
        console.log('🗑️ RoomManagement - Full API response:', result);
        
        // Call onRoomUpdate to trigger WebSocket and parent state updates
        console.log('🔄 Calling onRoomUpdate with:', result.room);
        console.log('🔄 Room data being passed:', {
          roomId: result.room._id,
          memberCount: result.room.members?.length,
          members: result.room.members?.map(m => ({ id: m._id, name: m.name, email: m.email })),
          adminCount: result.room.admins?.length,
          admins: result.room.admins?.map(a => ({ id: a._id, name: a.name, email: a.email }))
        });
        onRoomUpdate && onRoomUpdate(result.room);
        
        // Force update local state immediately with new object reference
        const updatedRoom = { ...result.room, _forceUpdate: Date.now() };
        console.log('🔄 Setting liveRoom to:', updatedRoom);
        setLiveRoom(updatedRoom);
        
        // Force a re-render by updating the force render counter
        setForceRender(prev => prev + 1);
        setTimeout(() => {
          setForceRender(prev => prev + 1);
        }, 100);
        
        alert('Member removed successfully');
      } else {
        const errorText = await response.text();
        console.error('🗑️ RoomManagement - Remove member error response:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          body: errorText
        });
        
        try {
          const error = JSON.parse(errorText);
          console.error('🗑️ RoomManagement - Remove member error:', error);
          alert(error.message || 'Failed to remove member');
        } catch (parseError) {
          console.error('🗑️ RoomManagement - Error parsing response:', parseError);
          alert('Failed to remove member - Invalid response from server');
        }
      }
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!liveRoom?._id) return;
    if (!window.confirm('Exit this group? You will stop receiving messages until re‑added.')) return;
    try {
      const url = `${API_BASE}/api/chat/rooms/${liveRoom._id}/members/${user._id || user.id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` }
      });
      if (response.ok) {
        alert('You have left the group');
        onClose && onClose();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.message || 'Failed to leave group');
      }
    } catch (e) {
      alert('Failed to leave group');
    }
  };

  const handleCopyInviteLink = async () => {
    // Create a shareable link that will trigger the join endpoint
    const link = `${window.location.origin}/chat/join/${liveRoom?._id}`;
    try {
      await navigator.clipboard.writeText(link);
      alert('Invite link copied to clipboard!\n\nShare this link with others to let them join the group.');
    } catch {
      prompt('Copy this invite link', link);
    }
  };

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API_BASE}/api/chat/users`, {
        headers: { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const users = await response.json();
        // Filter out users who are already members
        const currentMemberIds = liveRoom?.members?.map(member => member._id?.toString()) || [];
        const availableUsers = users.filter(user => !currentMemberIds.includes(user._id?.toString()));
        setAllUsers(availableUsers);
      } else {
        alert('Failed to fetch users');
      }
    } catch (error) {
      alert('Failed to fetch users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSelect = (userId, event) => {
    // Prevent the click from bubbling up to the parent div
    event.stopPropagation();
    
    setSelectedUsers(prev => {
      const newSelection = prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId];
      
      return newSelection;
    });
  };

  const handleAddSingleUser = async (userId) => {
    const selectedUser = allUsers.find(user => user._id === userId);
    if (!selectedUser) return;
    
    const confirmAdd = window.confirm(
      `Add "${selectedUser.name || selectedUser.email}" to the group?\n\nClick OK to add now, or Cancel to continue selecting more users.`
    );
    
    if (confirmAdd) {
      try {
        const response = await fetch(`${API_BASE}/api/chat/rooms/${liveRoom._id}/members`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ userIds: [userId] })
        });
        
        if (response.ok) {
          const result = await response.json();
          onRoomUpdate && onRoomUpdate(result.room);
          setLiveRoom({ ...result.room, _forceUpdate: Date.now() });
          setForceRender(prev => prev + 1);
          setSelectedUsers([]);
          alert('Member added successfully');
        } else {
          const error = await response.json().catch(() => ({}));
          alert(error.message || 'Failed to add member');
        }
      } catch (error) {
        alert('Failed to add member');
      }
    }
  };

  const handleAddSelectedUsers = async () => {
    if (selectedUsers.length === 0) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/chat/rooms/${liveRoom._id}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds: selectedUsers })
      });
      
      if (response.ok) {
        const result = await response.json();
        onRoomUpdate && onRoomUpdate(result.room);
        setLiveRoom({ ...result.room, _forceUpdate: Date.now() });
        setForceRender(prev => prev + 1);
        setShowAddMembersPanel(false);
        setSelectedUsers([]);
        alert('Members added successfully');
      } else {
        const error = await response.json().catch(() => ({}));
        alert(error.message || 'Failed to add members');
      }
    } catch (error) {
      alert('Failed to add members');
    }
  };

  const handleClearChats = async () => {
    if (!window.confirm('Are you sure you want to clear all messages from this room? This action cannot be undone, but the room and members will remain.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/chat/rooms/${liveRoom._id}/messages`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Successfully cleared ${result.deletedCount} messages from the room.`);
        // Refresh the room data to reflect the changes
        if (onRoomUpdate) {
          // Fetch updated room data
          const roomResponse = await fetch(`${API_BASE}/api/chat/rooms/${liveRoom._id}`, {
            headers: {
              'Authorization': `Bearer ${token || localStorage.getItem('token')}`
            }
          });
          if (roomResponse.ok) {
            const roomData = await roomResponse.json();
            onRoomUpdate(roomData.room);
          }
        }
      } else {
        const error = await response.json();
        alert(error.message);
      }
    } catch (error) {
      console.error('Error clearing chats:', error);
      alert('Failed to clear chat messages');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRoom = async () => {
    if (!window.confirm('Are you sure you want to delete this room? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/chat/rooms/${liveRoom._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Room deleted successfully');
        
        // Notify parent component that room was deleted
        if (onRoomDelete) {
          onRoomDelete(liveRoom._id);
        } else {
          // Fallback to just closing if no delete handler provided
          onClose();
        }
      } else {
        const error = await response.json();
        alert(error.message);
      }
    } catch (error) {
      console.error('Error deleting room:', error);
      alert('Failed to delete room');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  if (isCreating) {
    return (
      <div className="room-management-modal">
        <div className="room-management-content">
          <div className="modal-header">
            <h3>Create New Room</h3>
            <button 
              className="modal-close-btn"
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          </div>
          
          <form onSubmit={handleCreateRoom}>
            <div className="form-group">
              <label>Room Name *</label>
              <input
                type="text"
                placeholder="Enter room name"
                value={createForm.name}
                onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                required
                minLength={2}
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                placeholder="Enter room description"
                value={createForm.description}
                onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                maxLength={500}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Room Type</label>
              <select
                value={createForm.type}
                onChange={(e) => setCreateForm({...createForm, type: e.target.value})}
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={createForm.isPrivate}
                  onChange={(e) => setCreateForm({...createForm, isPrivate: e.target.checked})}
                />
                Private Room
              </label>
            </div>

            <div className="form-group">
              <label>Add Members</label>
              <div className="user-selection">
                {usersLoading ? (
                  <p>Loading users...</p>
                ) : (
                  users.map(user => (
                    <label key={user._id} className="user-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user._id)}
                        onChange={() => toggleUserSelection(user._id)}
                      />
                      <div className="user-info">
                        <span className="user-name">{user.name || 'Unknown User'}</span>
                        <span className="user-email">{user.email}</span>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" disabled={loading || !createForm.name.trim()}>
                {loading ? 'Creating...' : 'Create Room'}
              </button>
              <button type="button" onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (!liveRoom) {
    return (
      <div className="room-management-modal">
        <div className="room-management-content">
          <div className="modal-header">
            <h3>Room Management</h3>
            <button 
              className="modal-close-btn"
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          </div>
          <p>No room selected</p>
          <button onClick={() => setIsCreating(true)} className="create-room-btn">
            Create New Room
          </button>
          <button onClick={onClose} className="close-button">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="room-management-modal">
      <div className="room-management-content">
        <div className="modal-header">
          <h3>Room Management</h3>
          <button 
            className="modal-close-btn"
            onClick={onClose}
            title="Close"
          >
            ✕
          </button>
        </div>
        
        {/* Room Info */}
        <div className="room-info">
          <h4>{liveRoom.name}</h4>
          <p className="room-description">{liveRoom.description || 'No description'}</p>
          <div className="room-meta">
            <span className="room-type">{liveRoom.isPrivate ? 'Private' : 'Public'}</span>
            <span className="member-count">
              {liveRoom.memberCount || (liveRoom.members ? liveRoom.members.length : 0)} members
            </span>
            <span className="room-created">
              Created by {liveRoom.creator?.name || 'Unknown'}
            </span>
          </div>
        </div>

        {/* Edit Room (Admin only) */}
        {isAdmin && (
          <div className="edit-room">
            <h4>Edit Room</h4>
            {isEditing ? (
              <form onSubmit={handleEdit}>
                <div className="form-group">
                  <label>Room Name</label>
                  <input
                    type="text"
                    placeholder="Room name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    required
                    minLength={2}
                    maxLength={100}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    placeholder="Room description"
                    value={editForm.description}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    maxLength={500}
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editForm.isPrivate}
                      onChange={(e) => setEditForm({...editForm, isPrivate: e.target.checked})}
                    />
                    Private room
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button type="button" onClick={() => setIsEditing(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button onClick={() => setIsEditing(true)} className="edit-btn">
                Edit Room
              </button>
            )}
          </div>
        )}


        {/* Action Rows */}
        <div className="action-rows">
          <div className="action-row" onClick={() => { setShowAddMembersPanel(true); fetchAllUsers(); }}>
            <svg className="icon" viewBox="0 0 24 24" fill="currentColor"><path d="M15,14C12.33,14 7,15.34 7,18V20H23V18C23,15.34 17.67,14 15,14M4,13V10H1V8H4V5H6V8H9V10H6V13H4M15,12A4,4 0 0,0 19,8A4,4 0 0,0 15,4A4,4 0 0,0 11,8A4,4 0 0,0 15,12Z"/></svg>
            <span className="label">Add member</span>
          </div>
          <div className="action-row" onClick={handleCopyInviteLink}>
            <svg className="icon" viewBox="0 0 24 24" fill="currentColor"><path d="M10.59,13.41L9.17,12L14.17,7H11V5H17V11H15V7.83M5,5H13V7H5V19H19V11H21V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V7A2,2 0 0,1 5,5Z"/></svg>
            <span className="label">Invite to group via link</span>
          </div>
        </div>

        {/* Add Members Panel */}
        {showAddMembersPanel && (
          <div className="add-members-panel">
            <div className="panel-header">
              <h4>Add Member</h4>
              <button 
                className="close-btn" 
                onClick={() => { setShowAddMembersPanel(false); setSelectedUsers([]); }}
              >
                ×
              </button>
            </div>
            <div className="users-list">
              {loadingUsers ? (
                <div className="loading">Loading users...</div>
              ) : allUsers.length > 0 ? (
                allUsers.map(user => (
                  <div key={user._id} className="user-item">
                    <input 
                      type="checkbox" 
                      checked={selectedUsers.includes(user._id)}
                      onChange={(e) => handleUserSelect(user._id, e)}
                      className="user-checkbox"
                    />
                    <div className="user-info" onClick={() => handleAddSingleUser(user._id)}>
                      <div className="user-name">{user.name || 'Unknown User'}</div>
                      <div className="user-email">{user.email || 'No email'}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-users">No users available to add</div>
              )}
            </div>
            {selectedUsers.length > 0 && (
              <div className="panel-footer">
                <button 
                  className="add-selected-btn" 
                  onClick={handleAddSelectedUsers}
                >
                  Add {selectedUsers.length} member{selectedUsers.length > 1 ? 's' : ''}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Members List */}
        <div className="members-list" key={`members-${forceRender}`}>
          <h4>Members ({liveRoom.members ? liveRoom.members.length : 0})</h4>
          {liveRoom.members && liveRoom.members.length > 0 ? (
            liveRoom.members.map(member => {
              const isMemberAdmin = liveRoom.admins?.some(admin => admin._id?.toString() === member._id?.toString());
              const isMemberCreator = liveRoom.creator?._id?.toString() === member._id?.toString();
              const canActOnMember = isAdmin && !isMemberCreator;
              return (
                <div key={`${member._id}-${forceRender}`} className="member-item">
                  <div className="member-info">
                    <span className="member-name">{member.name || 'Unknown User'}</span>
                    <span className="member-email">{member.email || 'No email'}</span>
                    {isMemberAdmin && <span className="member-role admin">Group admin</span>}
                    {isMemberCreator && <span className="member-role creator">Creator</span>}
                  </div>
                  <div className="member-actions">
                    {canActOnMember && (
                      <button className="action-btn" onClick={() => setOpenMemberMenu(openMemberMenu === member._id ? null : member._id)}>⋯</button>
                    )}
                    {openMemberMenu === member._id && (
                      <div className="member-menu" onMouseLeave={() => setOpenMemberMenu(null)}>
                        {!isMemberAdmin && (
                          <div className="menu-item" onClick={() => { setOpenMemberMenu(null); handlePromoteToAdmin(member._id); }}>
                            <svg className="icon" viewBox="0 0 24 24" fill="currentColor"><path d="M12,5A3.5,3.5 0 0,1 15.5,8.5A3.5,3.5 0 0,1 12,12A3.5,3.5 0 0,1 8.5,8.5A3.5,3.5 0 0,1 12,5M12,14C15.33,14 22,15.67 22,19V21H2V19C2,15.67 8.67,14 12,14M19,3V5H22V7H19V10H17V7H14V5H17V3H19Z"/></svg>
                            Make group admin
                          </div>
                        )}
                        <div className="menu-item danger" onClick={() => { setOpenMemberMenu(null); handleRemoveMember(member._id); }}>
                          <svg className="icon" viewBox="0 0 24 24" fill="currentColor"><path d="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6Z"/></svg>
                          Remove
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="no-members">No members found</p>
          )}
        </div>

        {/* Secondary action rows */}
        <div className="action-rows">
          <div className="action-row" onClick={() => setIsFavourite((v) => !v)}>
            <svg className="icon" viewBox="0 0 24 24" fill="currentColor"><path d="M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.46,13.97L5.82,21L12,17.27Z"/></svg>
            <span className="label">Add to favourites</span>
          </div>
          <div className="action-row exit" onClick={handleLeaveGroup}>
            <svg className="icon" viewBox="0 0 24 24" fill="currentColor"><path d="M14,17V14H7V10H14V7L19,12L14,17M3,19V5A2,2 0 0,1 5,3H12A2,2 0 0,1 14,5H5V19H14A2,2 0 0,1 12,21H5A2,2 0 0,1 3,19Z"/></svg>
            <span className="label">Exit group</span>
          </div>
        </div>

        {/* Clear Chats (Admin only for regular rooms, any member for DMs) */}
        {isAdmin && (
          <div className="clear-chats">
            <h4>Clear Chat History</h4>
            <p>
              {room?.type === 'direct' 
                ? 'Remove all messages from this conversation. This action cannot be undone, but the conversation will remain.'
                : 'Remove all messages from this room. This action cannot be undone, but the room and members will remain.'
              }
            </p>
            <button 
              onClick={handleClearChats}
              disabled={loading}
              className="clear-chats-button"
            >
              {loading ? 'Clearing...' : 'Clear All Messages'}
            </button>
          </div>
        )}
        {!isAdmin && (
          <div style={{ padding: '10px', background: '#f0f0f0', margin: '10px 0', borderRadius: '4px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
              Debug: Clear chat section not shown because isAdmin = {String(isAdmin)}
            </p>
            <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#888' }}>
              Room type: {room?.type}, User ID: {user?._id || user?.id}
            </p>
            <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#888' }}>
              {room?.type === 'direct' 
                ? `DM members: ${room?.members?.map(m => m._id).join(', ')}`
                : `Room admins: ${room?.admins?.map(a => a._id).join(', ')}`
              }
            </p>
          </div>
        )}

        {/* Delete Room (Creator only) */}
        {isCreator && (
          <div className="delete-room">
            <h4>Danger Zone</h4>
            <p>This action cannot be undone. All messages and data will be permanently deleted.</p>
            <button 
              onClick={handleDeleteRoom}
              disabled={loading}
              className="delete-button"
            >
              {loading ? 'Deleting...' : 'Delete Room'}
            </button>
          </div>
        )}

        <div className="modal-actions">
          <button onClick={() => setIsCreating(true)} className="create-room-btn">
            Create New Room
          </button>
          <button onClick={onClose} className="close-button">Close</button>
        </div>
      </div>
    </div>
  );
};

export default RoomManagement;
